/**
 * Upsert-menu-item dialog (issue #156 sub-PR #5).
 *
 * Operator adds or replaces a single (plan, delivery_date, combo)
 * slot on a draft plan. Backend invariant INV-M2-2 (partial UNIQUE
 * on (plan_id, delivery_date, combo_id) live rows) ensures one
 * combo per day; the dialog surfaces the `slot_collision` 409
 * with extras so a re-pick of the same combo doesn't silently
 * overwrite.
 *
 * Drag-drop (the design doc's "7×2 calendar drag/drop" UX) is
 * deferred to a follow-up — adds `dnd-kit` dependency + a sidebar
 * combo palette + collision animation. This dialog ships the
 * keyboard-accessible alternative path so the week page is
 * functional immediately.
 */
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Modal,
  ModalBody,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Button,
  Label,
} from '@resource-kit/ui/react';
import { resourceKeys } from '@resource-kit/controllers';
import { useToast } from '@components/ui/Toast';
import { menuPlanAPI, comboAPI, type MenuItem } from '@/services';
import { queryKeys } from '@lib/queryKeys';
import { ApiError } from '@lib/api';

interface Props {
  open: boolean;
  onClose: () => void;
  planId: string;
  deliveryDate: string;
  /** Existing item if editing in-place; undefined when creating new. */
  existing?: MenuItem;
}

export function UpsertMenuItemDialog({ open, onClose, planId, deliveryDate, existing }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [comboId, setComboId] = useState<string>(existing?.combo_id ?? '');
  const [localError, setLocalError] = useState<string | null>(null);

  // Combo selector — limit to active combos. The catalog backend
  // accepts the same `status` filter the resource list page uses.
  const combosQuery = useQuery({
    queryKey: [...resourceKeys('catalog-combos').lists(), { status: 'active' }],
    queryFn: () =>
      comboAPI.list({
        limit: 100,
        extraParams: { status: 'active' },
      }),
    staleTime: 60_000,
    enabled: open,
  });

  const activeCombos = useMemo(() => combosQuery.data?.items ?? [], [combosQuery.data]);

  const reset = () => {
    setComboId(existing?.combo_id ?? '');
    setLocalError(null);
  };

  const handleClose = () => {
    if (mutation.isPending) return;
    reset();
    onClose();
  };

  const mutation = useMutation({
    mutationFn: () =>
      menuPlanAPI.upsertItem(planId, {
        delivery_date: deliveryDate,
        combo_id: comboId,
        // Preserve the existing snapshot when editing; backend
        // generates a fresh snapshot on publish, so the dialog
        // doesn't need to materialise dishes itself.
        ...(existing?.dishes ? { dishes: existing.dishes } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.menuPlans.detail(planId) });
      toast.success(
        existing ? t('menu_plan.upsertItem.successEdit') : t('menu_plan.upsertItem.successCreate'),
      );
      reset();
      onClose();
    },
    onError: (e) => {
      // The rebuilt backend returns a 409 with a human `message` for the
      // not-draft ("Plan is X, not draft") and slot-collision ("Slot
      // already filled ...") cases — no structured `extras` sentinels —
      // so surface the server message directly.
      if (e instanceof ApiError) {
        toast.error(e.message || t('menu_plan.upsertItem.error.unknown'));
        return;
      }
      toast.error(t('menu_plan.upsertItem.error.unknown'));
    },
  });

  const handleSubmit = () => {
    setLocalError(null);
    if (!comboId.trim()) {
      setLocalError(t('menu_plan.upsertItem.error.comboRequired'));
      return;
    }
    mutation.mutate();
  };

  return (
    <Modal isOpen={open} onClose={handleClose} size="sm">
      <ModalHeader onClose={handleClose}>
        <ModalTitle>
          {existing ? t('menu_plan.upsertItem.titleEdit') : t('menu_plan.upsertItem.titleCreate')}
        </ModalTitle>
        <ModalDescription>
          {t('menu_plan.upsertItem.description', { deliveryDate })}
        </ModalDescription>
      </ModalHeader>
      <ModalBody>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="upsert-combo">{t('menu_plan.upsertItem.comboLabel')}</Label>
            <select
              id="upsert-combo"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={comboId}
              onChange={(e) => {
                setComboId(e.target.value);
                setLocalError(null);
              }}
              disabled={mutation.isPending || combosQuery.isLoading}
              data-testid="menu-plan-upsert-combo"
            >
              <option value="">
                {combosQuery.isLoading
                  ? t('menu_plan.upsertItem.comboLoading')
                  : t('menu_plan.upsertItem.comboPlaceholder')}
              </option>
              {activeCombos.map((combo) => (
                <option key={combo.id} value={combo.id}>
                  {combo.code} · {combo.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">{t('menu_plan.upsertItem.comboHint')}</p>
          </div>
          {localError && (
            <p className="text-sm text-destructive" role="alert">
              {localError}
            </p>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={handleClose} disabled={mutation.isPending}>
          {t('ui.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={mutation.isPending}
          data-testid="menu-plan-upsert-submit"
        >
          {mutation.isPending
            ? t('menu_plan.upsertItem.submitting')
            : existing
              ? t('menu_plan.upsertItem.submitEdit')
              : t('menu_plan.upsertItem.submitCreate')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
